import apiClient from './api';

class BusinessPolicyService {
  /**
   * Get all business policies with optional filters
   * @param {Object} params - Query parameters {policy_type, status, is_active, search, ordering, page}
   * @returns {Promise} - List of business policies
   */
  async getPolicies(params = {}) {
    try {
      const response = await apiClient.get('/admin/business-policies/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching business policies:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create business policy as draft
   * @param {Object} data - {policy_type, version, content, is_active}
   * @returns {Promise} - Created business policy
   */
  async createPolicy(data) {
    try {
      const response = await apiClient.post('/admin/business-policies/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create and publish business policy immediately
   * @param {Object} data - {policy_type, version, content, is_active}
   * @returns {Promise} - Created and published business policy
   */
  async createAndPublishPolicy(data) {
    try {
      const response = await apiClient.post('/admin/business-policies/create_and_publish/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating and publishing business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get single business policy by ID
   * @param {string} id - Policy ID (UUID)
   * @returns {Promise} - Business policy details
   */
  async getPolicyById(id) {
    try {
      const response = await apiClient.get(`/admin/business-policies/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update business policy (full update)
   * @param {string} id - Policy ID (UUID)
   * @param {Object} data - Complete business policy data
   * @returns {Promise} - Updated business policy
   */
  async updatePolicy(id, data) {
    try {
      const response = await apiClient.put(`/admin/business-policies/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Partial update business policy
   * @param {string} id - Policy ID (UUID)
   * @param {Object} data - Partial business policy data
   * @returns {Promise} - Updated business policy
   */
  async patchPolicy(id, data) {
    try {
      const response = await apiClient.patch(`/admin/business-policies/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error patching business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete business policy
   * @param {string} id - Policy ID (UUID)
   * @returns {Promise} - Success status
   */
  async deletePolicy(id) {
    try {
      await apiClient.delete(`/admin/business-policies/${id}/`);

      return {
        success: true,
        message: 'Business policy deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Publish business policy (change status from draft to published)
   * @param {string} id - Policy ID (UUID)
   * @returns {Promise} - Published business policy
   */
  async publishPolicy(id) {
    try {
      const response = await apiClient.post(`/admin/business-policies/${id}/publish/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error publishing business policy:', error);
      return this.handleError(error);
    }
  }

  /**
   * Toggle active status of business policy
   * @param {string} id - Policy ID (UUID)
   * @returns {Promise} - Updated business policy
   */
  async toggleActive(id) {
    try {
      const response = await apiClient.post(`/admin/business-policies/${id}/toggle_active/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error toggling business policy active status:', error);
      return this.handleError(error);
    }
  }

  /**
   * Export business policies to Excel
   * @returns {Promise} - Excel file blob
   */
  async exportToExcel() {
    try {
      const response = await apiClient.get('/admin/business-policies/export_excel/', {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error exporting business policies to Excel:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      if (error.response.data) {
        // Extract error message from response
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else {
          // Try to get first error from field errors
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          }
        }
      }

      // Handle specific status codes
      if (error.response.status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (error.response.status === 404) {
        message = 'Business policy not found.';
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new BusinessPolicyService();
